import React, { useState } from 'react';
import Select from 'react-select';
import PasswordInput from '../../components/common/PasswordInput';

/**
 * Job role catalogue.
 *
 * A role is only a descriptive job title - it carries no permissions on its own.
 * Permissions come from `accessLevel` below, which is why new roles can be added
 * here without touching any route guard.
 */
export const ROLE_GROUPS = [
  {
    label: 'Internal Roles',
    department: 'internal',
    options: [
      { value: 'org_admin', label: 'Organisation Admin', icon: 'fa-user-shield', color: '#1d4ed8' },
      { value: 'project_manager', label: 'Project Manager', icon: 'fa-user-tie', color: '#3b82f6' },
      { value: 'hse_manager', label: 'HSE Manager', icon: 'fa-hard-hat', color: '#16a34a' },
      { value: 'environmental_officer', label: 'Environmental Officer', icon: 'fa-leaf', color: '#10b981' },
      { value: 'safety_officer', label: 'Safety Officer', icon: 'fa-shield-alt', color: '#f59e0b' },
      { value: 'field_officer', label: 'Field Officer', icon: 'fa-map-marked-alt', color: '#0ea5e9' },
      { value: 'field_inspector', label: 'Field Inspector', icon: 'fa-clipboard-check', color: '#8b5cf6' },
      { value: 'community_liaison', label: 'Community Liaison Officer', icon: 'fa-users', color: '#ec4899' },
      { value: 'compliance_officer', label: 'Compliance Officer', icon: 'fa-balance-scale', color: '#6366f1' },
      { value: 'site_supervisor', label: 'Site Supervisor', icon: 'fa-tasks', color: '#14b8a6' },
      { value: 'viewer', label: 'Viewer', icon: 'fa-eye', color: '#6b7280' },
    ],
  },
  {
    label: 'External Stakeholders',
    department: 'external',
    options: [
      { value: 'donor_rep', label: 'Donor Representative', icon: 'fa-handshake', color: '#0891b2' },
      { value: 'government_authority', label: 'Government Authority', icon: 'fa-landmark', color: '#dc2626' },
      { value: 'regulator_admin', label: 'Regulator Admin', icon: 'fa-gavel', color: '#b91c1c' },
      { value: 'contractor_rep', label: 'Contractor Representative', icon: 'fa-building', color: '#64748b' },
      { value: 'auditor', label: 'Auditor / Consultant', icon: 'fa-user-check', color: '#7c3aed' },
    ],
  },
  {
    label: 'Something else',
    department: null,
    options: [
      { value: 'other', label: 'Other (specify a title)', icon: 'fa-pen', color: '#6b7280' },
    ],
  },
];

const SELECTABLE_ROLES = ROLE_GROUPS.reduce((all, group) => all.concat(group.options), []);

// Platform-level role that is never assignable from this screen, but still has
// to render correctly if such a user shows up in the list.
const NON_SELECTABLE_ROLES = [
  { value: 'super_admin', label: 'Super Admin', icon: 'fa-crown', color: '#111827' },
];

const FALLBACK_ROLE = { label: 'Unassigned', icon: 'fa-user', color: '#6b7280' };

/**
 * Look up the display metadata for a stored role value. Falls back to the raw
 * value so an unknown role added on the backend still renders sensibly.
 */
export const getRoleInfo = (roleValue, customRoleTitle = null) => {
  const known =
    SELECTABLE_ROLES.find((r) => r.value === roleValue) ||
    NON_SELECTABLE_ROLES.find((r) => r.value === roleValue);

  if (!known) {
    if (!roleValue) return FALLBACK_ROLE;
    return {
      ...FALLBACK_ROLE,
      label: roleValue.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    };
  }

  // 'other' roles carry their real title in a free-text column
  if (roleValue === 'other' && customRoleTitle) {
    return { ...known, label: customRoleTitle };
  }

  return known;
};

/**
 * Access levels are what actually grant permissions.
 */
export const ACCESS_LEVELS = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full control: manage the organisation, team members, billing and every project.',
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Can create and edit projects, inspections and incidents, and assign work to others.',
  },
  {
    value: 'contributor',
    label: 'Contributor',
    description: 'Can record inspections and report incidents on the projects they are assigned to.',
  },
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read-only: can view dashboards and reports but cannot change anything.',
  },
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  customRoleTitle: '',
  accessLevel: 'viewer',
  department: 'internal',
  projects: [],
  certifications: [],
  passwordMode: 'invite',
  password: '',
};

/**
 * Shared add/edit member modal.
 *
 * This MUST stay a top-level component. It used to be declared inside `Team`,
 * which made React see a brand new component type on every render, remount the
 * subtree and drop input focus on every keystroke.
 */
const MemberFormModal = ({
  isOpen,
  onClose,
  onSave,
  saving = false,
  editMode = false,
  initialData = null,
  projectOptions = [],
  projectsLoading = false,
}) => {
  const [formData, setFormData] = useState(
    initialData ? { ...EMPTY_FORM, ...initialData } : EMPTY_FORM
  );
  const [certificationInput, setCertificationInput] = useState('');
  const [formError, setFormError] = useState('');

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addCertification = () => {
    const value = certificationInput.trim();
    if (!value) return;
    if (formData.certifications.includes(value)) {
      setCertificationInput('');
      return;
    }
    setFormData((prev) => ({ ...prev, certifications: [...prev.certifications, value] }));
    setCertificationInput('');
  };

  const removeCertification = (index) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  const handleCertificationKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCertification();
    }
  };

  const selectedRole = SELECTABLE_ROLES.find((r) => r.value === formData.role) || null;
  const selectedAccessLevel =
    ACCESS_LEVELS.find((l) => l.value === formData.accessLevel) || ACCESS_LEVELS[3];
  const selectedProjects = projectOptions.filter((p) => formData.projects.includes(p.value));

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();
    if (!firstName) {
      setFormError('Please enter the member\u2019s first name.');
      return;
    }
    if (!lastName) {
      setFormError('Please enter the member\u2019s last name.');
      return;
    }
    if (!formData.role) {
      setFormError('Please choose a role.');
      return;
    }
    if (formData.role === 'other' && !formData.customRoleTitle.trim()) {
      setFormError('Please enter a job title for the "Other" role.');
      return;
    }
    if (!editMode && formData.passwordMode === 'set_password' && formData.password.length < 8) {
      setFormError('The initial password must be at least 8 characters.');
      return;
    }

    onSave({
      firstName,
      lastName,
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      role: formData.role,
      customRoleTitle: formData.customRoleTitle.trim(),
      accessLevel: formData.accessLevel,
      department: formData.department,
      certifications: formData.certifications,
      permissions: { projects: formData.projects },
      inviteMode: formData.passwordMode,
      password: formData.password,
    });
  };

  return (
    <div className="team-modal-overlay" onClick={onClose}>
      <div className="team-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className={`fas ${editMode ? 'fa-user-edit' : 'fa-user-plus'}`}></i>
            {editMode ? 'Edit Team Member' : 'Add Team Member'}
          </h2>
          <button type="button" className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {formError && (
              <div className="modal-alert error">
                <i className="fas fa-exclamation-circle"></i>
                <span>{formError}</span>
              </div>
            )}

            {/* Personal Information */}
            <div className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="member-first-name">First Name *</label>
                  <input
                    id="member-first-name"
                    type="text"
                    name="firstName"
                    placeholder="e.g. Sarah"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="member-last-name">Last Name *</label>
                  <input
                    id="member-last-name"
                    type="text"
                    name="lastName"
                    placeholder="e.g. Nambi"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="member-email">Email Address *</label>
                  <input
                    id="member-email"
                    type="email"
                    name="email"
                    placeholder="e.g. sarah.nambi@company.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="email"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="member-phone">Phone Number</label>
                  <input
                    id="member-phone"
                    type="tel"
                    name="phone"
                    placeholder="e.g. +256 700 123 456"
                    value={formData.phone}
                    onChange={handleInputChange}
                    autoComplete="tel"
                  />
                </div>
              </div>

              <div className="form-row full-width">
                <div className="form-group">
                  <label htmlFor="member-department">Department *</label>
                  <select
                    id="member-department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="internal">Internal Staff</option>
                    <option value="external">External Stakeholder</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Role & Access */}
            <div className="form-section">
              <h3>Role &amp; Access</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="member-role">Job Role *</label>
                  <Select
                    inputId="member-role"
                    className="role-select"
                    classNamePrefix="select"
                    options={ROLE_GROUPS}
                    value={selectedRole}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, role: selected ? selected.value : '' }))
                    }
                    placeholder="Select a role..."
                    isClearable
                  />
                  <small className="form-hint">
                    A job title only. Permissions come from the access level.
                  </small>
                </div>
                <div className="form-group">
                  <label htmlFor="member-access-level">Access Level *</label>
                  <select
                    id="member-access-level"
                    name="accessLevel"
                    value={formData.accessLevel}
                    onChange={handleInputChange}
                    required
                  >
                    {ACCESS_LEVELS.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-hint">{selectedAccessLevel.description}</small>
                </div>
              </div>

              {formData.role === 'other' && (
                <div className="form-group">
                  <label htmlFor="member-custom-role">Job Title *</label>
                  <input
                    id="member-custom-role"
                    type="text"
                    name="customRoleTitle"
                    placeholder="e.g. Waste Management Coordinator"
                    value={formData.customRoleTitle}
                    onChange={handleInputChange}
                  />
                  <small className="form-hint">
                    Shown across the app in place of the generic &ldquo;Other&rdquo; label.
                  </small>
                </div>
              )}
            </div>

            {/* Project Assignment */}
            <div className="form-section">
              <h3>Project Assignment</h3>
              <div className="form-group">
                <label htmlFor="member-projects">Assigned Projects</label>
                <Select
                  inputId="member-projects"
                  className="project-select"
                  classNamePrefix="select"
                  options={projectOptions}
                  value={selectedProjects}
                  onChange={(selected) =>
                    setFormData((prev) => ({
                      ...prev,
                      projects: (selected || []).map((option) => option.value),
                    }))
                  }
                  placeholder={projectsLoading ? 'Loading projects...' : 'Select projects...'}
                  isLoading={projectsLoading}
                  noOptionsMessage={() => 'No projects available yet'}
                  isMulti
                />
                <small className="form-hint">
                  Select which projects this member can access. Leave empty for none.
                </small>
              </div>
            </div>

            {/* Certifications */}
            <div className="form-section">
              <h3>Certifications &amp; Qualifications</h3>
              <div className="form-group">
                <label htmlFor="member-certification">Certifications (Optional)</label>
                <div className="tag-input-row">
                  <input
                    id="member-certification"
                    type="text"
                    placeholder="e.g. NEBOSH IGC"
                    value={certificationInput}
                    onChange={(e) => setCertificationInput(e.target.value)}
                    onKeyDown={handleCertificationKeyDown}
                  />
                  <button type="button" className="btn-add-tag" onClick={addCertification}>
                    <i className="fas fa-plus"></i> Add
                  </button>
                </div>
                <small className="form-hint">Press Enter or click Add for each certification.</small>

                {formData.certifications.length > 0 && (
                  <div className="tag-list">
                    {formData.certifications.map((certification, index) => (
                      <span className="tag-chip" key={certification}>
                        <i className="fas fa-certificate"></i>
                        {certification}
                        <button
                          type="button"
                          className="tag-remove"
                          onClick={() => removeCertification(index)}
                          title={`Remove ${certification}`}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Password - creation only */}
            {!editMode && (
              <div className="form-section">
                <h3>Account Access</h3>
                <div className="password-options">
                  <label
                    className={`password-option ${formData.passwordMode === 'invite' ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="passwordMode"
                      value="invite"
                      checked={formData.passwordMode === 'invite'}
                      onChange={handleInputChange}
                    />
                    <div className="password-option-content">
                      <h4>
                        <i className="fas fa-paper-plane"></i> Send a setup link
                      </h4>
                      <p>
                        We email them a link, valid for 24 hours, so they choose their own password.
                      </p>
                    </div>
                  </label>

                  <label
                    className={`password-option ${formData.passwordMode === 'set_password' ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="passwordMode"
                      value="set_password"
                      checked={formData.passwordMode === 'set_password'}
                      onChange={handleInputChange}
                    />
                    <div className="password-option-content">
                      <h4>
                        <i className="fas fa-key"></i> Set an initial password now
                      </h4>
                      <p>They can sign in immediately, and should change it afterwards.</p>
                    </div>
                  </label>
                </div>

                {formData.passwordMode === 'set_password' && (
                  <div className="form-group">
                    <label htmlFor="member-password">Initial Password *</label>
                    <PasswordInput
                      id="member-password"
                      name="password"
                      placeholder="Min. 8 characters"
                      value={formData.password}
                      onChange={handleInputChange}
                      minLength={8}
                      autoComplete="new-password"
                      required
                    />
                    <small className="form-hint">
                      Share this with the member over a secure channel.
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  {editMode ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  <i className={`fas ${editMode ? 'fa-save' : 'fa-user-plus'}`}></i>
                  {editMode ? 'Save Changes' : 'Add Member'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MemberFormModal;
